import {PaymentAttemptStatus, PaymentAttempt, Callbacks, PaymentIntent} from '@/internal/payment-intent/types';
import PaypalHandler from '@/plugins/payments/paypal_express_checkout/handlers';
import {Funding, Options} from '../types';
import Helpers from '@/helpers';
import {loadScriptUsingPredicate} from '@/extensions/three_domain_secure/common/utils';
import qs from 'qs';

const Interval = 2000;
const MaxRetries = 2;

export default class PaypalExpressCheckoutHandler extends PaypalHandler {
  constructor(handler: PaypalHandler, ...args) {
    super(...args);
  }

  paypal() {
    const paypal_namespace = 'paypal_' + this.getPaymentIntent().gateway_account_id;
    return window[paypal_namespace];
  }

  handlePayment(callbacks: Callbacks): Promise<PaymentIntent> {
    this.initCallbacks({}, callbacks);
    return new Promise<PaymentIntent>((resolve, reject) => {
      this.callbackHandler.setPromiseResolvers(resolve, reject);
    });
  }

  async mountPaymentButton(id: string, options: Options): Promise<any> {
    const response = await this.fetchGatewayCredential();
    if (response && response.client_id && response.merchant_id) {
      this.kvl({
        action: 'paypal_sdk',
        result: 'true',
        payment_intent_id: this.getPaymentIntent().id,
      });
      return this.renderPaypalButton(id, options, response.client_id, response.merchant_id);
    } else {
      return this.renderCheckoutButton(id, options);
    }
  }

  renderCheckoutButton(id: string, options: Options) {
    return Promise.all([this.loadPaypalJS()]).then(() => {
      // To fix Document is ready and element #paypal-button does not exist error
      if (window.document.querySelector(id)) {
        return this.renderButton(id, options);
      } else {
        return new Promise((resolve, reject) => {
          let retryCount = 0;
          const myInterval = setInterval(() => {
            if (document.querySelector(id) || retryCount > MaxRetries) {
              this.kvl({
                action: 'paypal_button_found',
                retry: retryCount,
                result: document.querySelector(id) ? 'true' : 'false',
                payment_intent_id: this.getPaymentIntent().id,
              });
              window.clearInterval(myInterval);
              resolve(this.renderButton(id, options));
            }
            retryCount++;
          }, Interval);
        });
      }
    });
  }

  renderButton(id, options: Options) {
    return this.paypal().Button.render(
      {
        ...this.getConfigurationOptions(options),
        env: Helpers.isTestSite() ? 'sandbox' : 'production',
        payment: () => {
          return this.initPayment().then((data) => this.confirmPayment(data));
        },
        onAuthorize: (data, actions) => {
          return this.confirmPayment();
        },
        onCancel: () => {
          this.cancelPayment('payment cancelled');
        },
        onClick: async () => {
          await this.callbackHandler.triggerClickCallback();
        },
        onError: (err) => {
          this.kvl({
            action: 'paypal_button_exist',
            result: document.querySelector(id) ? 'true' : 'false',
            payment_intent_id: this.getPaymentIntent().id,
          });
          if (
            (err.name && err.name == 'Error' && err.message && err.message.includes('Window is closed')) ||
            (err.name && err.name == 'Cancel')
          ) {
            return this.cancelPayment('payment cancelled');
          } else {
            return this.callbackHandler.triggerErrorCallback(err);
          }
        },
      },
      id
    );
  }

  renderPaypalButton(id: string, options: Options, clientID: string, merchantID: string) {
    return this.loadPaypalSdk(clientID, merchantID, options).then(() => {
      this.kvl({
        action: 'paypal_js_loaded',
        payment_intent_id: this.getPaymentIntent().id,
      });

      const buttonInstance = this.paypal().Buttons({
        fundingSource: this.paypal().FUNDING.PAYPAL,
        style: options.style,
        createBillingAgreement: (data, actions) => {
          return this.initPayment().then((data) => this.confirmPayment(data));
        },
        onApprove: (data, actions) => {
          this.kvl({
            action: 'paypal_on_approve',
            payment_intent_id: this.getPaymentIntent().id,
          });
          return this.confirmPayment();
        },
        onCancel: () => {
          this.kvl({
            action: 'paypal_on_cancel',
            payment_intent_id: this.getPaymentIntent().id,
          });
          this.cancelPayment('payment cancelled');
        },
        onClick: (data, actions) => {
          this.kvl({
            action: 'paypal_button_clicked',
            payment_intent_id: this.getPaymentIntent().id,
          });
          return this.handleClick(data, actions);
        },
        onError: (err) => {
          this.kvl({
            action: 'paypal_error',
            result: err && typeof err === 'object' ? JSON.stringify(err) : String(err),
            payment_intent_id: this.getPaymentIntent().id,
          });
          if (
            (err.name && err.name == 'Error' && err.message && err.message.includes('Window is closed')) ||
            (err.name && err.name == 'Cancel')
          ) {
            return this.cancelPayment('payment cancelled');
          } else {
            return this.callbackHandler.triggerErrorCallback(err);
          }
        },
      });

      this.kvl({
        action: 'paypal_button_created',
        payment_intent_id: this.getPaymentIntent().id,
      });

      return buttonInstance.render(id);
    });
  }

  handlePaymentAttempt(paymentAttempt: PaymentAttempt): Promise<any> {
    switch (paymentAttempt.status) {
      case PaymentAttemptStatus.REQUIRES_CHALLENGE:
        return Promise.resolve(paymentAttempt.id_at_gateway);
    }
  }

  getConfigurationOptions(options: Options) {
    let customiseOptions = {
      style: options.style,
      funding: this.getPaypalFunding(options),
    };
    if (options.locale) {
      customiseOptions['locale'] = options.locale;
    }
    return customiseOptions;
  }

  getPaypalFunding(options): Funding {
    let funds: string[] = [];
    if (this.paypal() && this.paypal().FUNDING) {
      funds = Object.values(this.paypal().FUNDING).filter((f) => f != this.paypal().FUNDING.PAYPAL) as string[];
    }
    return Object.assign({disallowed: funds}, options.funding);
  }

  loadPaypalSdk(clientID: string, merchantID: string, options: Options): Promise<any> {
    const {allowed, disallowed} = options.funding || {};
    const queryString = qs.stringify({
      ...(disallowed ? {'disable-funding': disallowed.join()} : {}),
      ...(allowed ? {'enable-funding': allowed.join()} : {}),
      ...(options.locale ? {locale: options.locale} : {}),
      'client-id': clientID,
      'merchant-id': merchantID,
      currency: this.getPaymentIntent().currency_code,
      vault: true,
      intent: 'tokenize',
    });
    if (!(this.paypal() && this.paypal().Buttons)) {
      return loadScriptUsingPredicate(
        `https://www.paypal.com/sdk/js?${queryString}`,
        () => !!(this.paypal() && this.paypal().Buttons),
        'paypal_' + this.getPaymentIntent().gateway_account_id
      );
    }
    return Promise.resolve(true);
  }
}
